import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createRetryLink } from './graphql/retryLink';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';

import auth from './Auth/Auth';
import { ENV_VARS_WRAPPER } from './configs/envVariables';
import { typePolicies } from './graphql/cache/typePolicies';
import { getFormattedErrorMessage } from './utils/errorFormatting';
import { isSilencedAccessDenial } from './utils/portfolioAccess';
import useUserStore from './store/user';

// this session id will be added to all graphql requests, this way we can track
// a full user session across all requests that a user makes while navigating our app
const sessionId = uuidv4();

enum NexoyaCustomHeader {
  SessionId = 'x-nexoya-session-id',
  Debug = 'x-nexoya-debug',
}

const DEBUG_LOCAL_STORAGE_KEY = 'debug';

function shouldSendDebugHeader() {
  return window.localStorage.getItem(DEBUG_LOCAL_STORAGE_KEY) === 'true';
}

const httpLink = new HttpLink({
  uri: (operation) => {
    const baseUrl = `${window[ENV_VARS_WRAPPER]?.REACT_APP_API_BASE_URL || ''}`;
    const operationName = operation.operationName;
    // Add operationName as a query parameter.
    // Using encodeURIComponent is a good practice.
    return `${baseUrl}?op=${encodeURIComponent(operationName)}`;
  },
});

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const token = auth.getAccessToken();

  // Check if we have a valid token and are authenticated
  if (!token || !auth.isAuthenticated()) {
    // If not authenticated, redirect to login
    // The Auth class already handles scheduled token renewal, so we don't need to call renewToken here
    auth.login({ signup: false, customQueryParams: null });
    return null;
  }

  // Additional safety check: ensure token is not empty or just whitespace
  if (!token.trim()) {
    auth.login({ signup: false, customQueryParams: null });
    return null;
  }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : null,
      [NexoyaCustomHeader.SessionId]: sessionId,
      ...(shouldSendDebugHeader() ? { [NexoyaCustomHeader.Debug]: 'true' } : {}),
    },
  }));

  return forward(operation);
});

const errorLink = onError(({ operation, graphQLErrors, networkError, response }) => {
  // Handle unauthorized errors (401) by redirecting to login - do this before showing error
  if (networkError && (networkError as ServerError).statusCode === 401) {
    auth.login({ signup: false, customQueryParams: null });
    return;
  }

  // Operations that show their own specific error toast (e.g. the perf-view bookmark/layout
  // mutations) set skipGlobalErrorToast on their context so we don't also fire the generic one.
  const skipGlobalErrorToast = operation?.getContext()?.skipGlobalErrorToast === true;

  // Collect all errors for logging
  if (networkError) {
    // eslint-disable-next-line no-console
    console.log(`[Network error]: ${networkError}`, {
      statusCode: (networkError as ServerError).statusCode,
      result: (networkError as ServerError).result,
      response,
    });
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions, path }) => {
      // eslint-disable-next-line no-console
      console.log(`[GraphQL error]: ${message}`, { extensions, path, response });
    });
  }

  // Create a synthetic ApolloError-like object to extract error messages
  const errorToFormat = {
    graphQLErrors: graphQLErrors || [],
    networkError: networkError || null,
    response: response || null,
  } as any;

  // Extract and format error message based on user role
  const isSupportUser = useUserStore.getState().isSupportUser;

  // Show toast for all errors (skip 401 as it's handled separately). Support users see the full
  // formatted message, everyone else a generic one. Mutations with their own onError toast set
  // skipGlobalErrorToast on their context (see graphql/performanceView/mutationErrorOptions).

  // A wrong-team portfolio fails every portfolio-scoped read on the page at once — measured at
  // six — and RequirePortfolioAccess recovers those by navigating to the list. See
  // isSilencedAccessDenial for why this must never extend to mutations.
  const isHandledAccessDenial = isSilencedAccessDenial(operation.query, errorToFormat);

  if (!skipGlobalErrorToast && !isHandledAccessDenial && (networkError || (graphQLErrors && graphQLErrors.length > 0))) {
    if (isSupportUser) {
      // Support users see the full error details
      const formattedMessage = getFormattedErrorMessage(errorToFormat);
      toast.error(formattedMessage);
    } else {
      // Regular users see a UX-friendly generic message
      toast.error('An internal error has occurred, please try again or reach to your specialist');
    }
  }
});

const client = new ApolloClient({
  // Lives in its own side-effect-free module so tests can build a cache with the real policies.
  cache: new InMemoryCache({ typePolicies }),
  // The retry sits BELOW errorLink on purpose: errorLink then toasts once, after the last
  // attempt, rather than once per attempt. Only operations opting in via `RETRYABLE_CONTEXT`
  // are retried — see `./graphql/retryLink`.
  link: from([authMiddleware, errorLink, createRetryLink(), httpLink]),
});

export default client;
